/**
 * Cart line-item star ratings.
 *
 * Renders inline star SVGs + review count inside any `.cart-drawer__item-stars`
 * element that carries a `data-product-id`. Mirrors the populateCartStarRatings
 * helper from JonesRoadBeauty-Main 8/app/components/cart/cartTemplate.js,
 * including the Junip store key and clip-path star pattern.
 *
 * The script auto-runs on:
 *   - DOMContentLoaded
 *   - liquid-ajax-cart:request-end (after any cart add/update/remove)
 *   - cart-open (when the drawer is opened)
 */

const JUNIP_STORE_KEY = '4NjxTTX33ohe7HAXvYJi8ccY';
const STAR_PATH = 'M5.36372 16.7222L5.73622 11.5359L0.585938 10.7443L4.87784 7.81986L2.2865 3.31213L7.27483 4.83087L9.18594 0L11.097 4.83087L16.0853 3.31213L13.4941 7.81986L17.7859 10.7443L12.6357 11.5359L13.0082 16.7222L9.18594 13.2L5.36372 16.7222Z';

const _ratingsCache = {};

// Overflow-based partial fills (no SVG clipPath) — clipPath breaks inside
// Swiper transforms, which is why some cart upsell cards showed counts only.
function buildStarsHtml(average, size = 10) {
  const avgNum = Number(average);
  const safeAvg = Number.isFinite(avgNum) ? Math.max(0, Math.min(5, avgNum)) : 0;
  let stars = '';
  for (let i = 0; i < 5; i += 1) {
    const fill = Math.max(0, Math.min(1, safeAvg - i));
    const fillPct = (fill * 100).toFixed(1);
    stars += `<span class="cart-drawer__item-star" style="width:${size}px;height:${size}px;">
      <svg width="${size}" height="${size}" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="${STAR_PATH}" fill="#d0d0d0"/></svg>
      ${fill > 0 ? `<span class="cart-drawer__item-star-fill" style="width:${fillPct}%;height:${size}px"><svg width="${size}" height="${size}" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="${STAR_PATH}" fill="#000"/></svg></span>` : ''}
    </span>`;
  }
  return `<span class="cart-drawer__item-stars-row">${stars}</span>`;
}

export function populateCartStarRatings() {
  // Skip anything already rendered server-side (data-stars-rendered="1" set in
  // Liquid from the Junip metafields). This is only a fallback for products
  // whose rating metafield isn't populated yet.
  const els = document.querySelectorAll(
    '.cart-drawer__item-stars[data-product-id]:not([data-stars-rendered="1"])'
  );
  if (!els.length) return;

  const productIds = [...new Set([...els].map((el) => el.dataset.productId).filter(Boolean))];
  if (!productIds.length) return;

  const uncachedIds = productIds.filter((id) => !(id in _ratingsCache));

  const applyRatings = () => {
    els.forEach((el) => {
      if (el.dataset.starsRendered === '1') return;
      const pid = el.dataset.productId;
      const cached = _ratingsCache[pid];
      if (cached && cached.count > 0) {
        const size = parseInt(el.dataset.starSize, 10) || 12;
        const formattedCount = cached.count.toLocaleString('en-US');
        el.innerHTML = `${buildStarsHtml(cached.average, size)}<span class="cart-drawer__item-stars-count">(${formattedCount})</span>`;
        el.dataset.starsRendered = '1';
      } else if (cached) {
        el.style.display = 'none';
        el.dataset.starsRendered = '1';
      }
    });
  };

  if (uncachedIds.length === 0) {
    applyRatings();
    return;
  }

  fetch(`https://api.juniphq.com/v1/products?filter[remote_ids]=${uncachedIds.join(',')}`, {
    headers: { 'Junip-Store-Key': JUNIP_STORE_KEY },
  })
    .then((r) => r.json())
    .then((data) => {
      (data.products || []).forEach((p) => {
        _ratingsCache[p.remote_id] = { average: p.rating_average, count: p.rating_count };
      });
      uncachedIds.forEach((id) => {
        if (!(id in _ratingsCache)) _ratingsCache[id] = { average: 0, count: 0 };
      });
      applyRatings();
    })
    .catch(() => {
      els.forEach((el) => {
        el.style.display = 'none';
      });
    });
}

if (typeof window !== 'undefined') {
  window.populateCartStarRatings = populateCartStarRatings;
}

function schedule() {
  requestAnimationFrame(() => populateCartStarRatings());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', schedule, { once: true });
} else {
  schedule();
}

document.addEventListener('liquid-ajax-cart:request-end', (event) => {
  if (event.detail?.requestState?.responseData?.ok) {
    schedule();
  }
});

window.addEventListener('cart-open', schedule);
